import { Fragment, type ReactElement } from 'react';

import type { PortfolioSection } from '@/entities/portfolio';

/**
 * 본문에 쓰는 최소 마크다운 — `**강조**` 와 `[글자](주소)` 둘뿐이다.
 * 마크다운 라이브러리를 통째로 들이지 않는다. 문단이 이 둘 말고 더 필요해지면
 * 그때는 파서를 늘리지 말고 라이브러리를 검토한다.
 */
const TOKEN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/;
const LINK = /^\[([^\]]+)\]\(([^)]+)\)$/;

export function RichText({ text }: { text: string }): ReactElement {
  return (
    <>
      {text.split(TOKEN).map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: 한 문단을 쪼갠 조각이라 순서가 곧 정체성이다
            <b key={index} className="font-semibold text-foreground">
              {part.slice(2, -2)}
            </b>
          );
        }

        const link = LINK.exec(part);
        if (link) {
          // 바깥 사이트로 나간다 — 읽던 문서를 닫지 않게 새 탭에서 연다
          return (
            <a
              // biome-ignore lint/suspicious/noArrayIndexKey: 위와 같다
              key={index}
              href={link[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-foreground underline underline-offset-4"
            >
              {link[1]}
            </a>
          );
        }

        return part;
      })}
    </>
  );
}

/**
 * 좌측 목차.
 *
 * 문서가 길어지면 「지금 어디쯤인지」와 「얼마나 남았는지」가 보여야 끝까지 읽는다.
 * 좁은 화면에서는 본문 위로 접어 올린다 — 감추면 이동 수단이 통째로 사라진다.
 */
export function DocToc({ sections }: { sections: readonly PortfolioSection[] }): ReactElement {
  return (
    <nav aria-label="목차" className="lg:sticky lg:top-24">
      <p className="mb-3 text-[11px] font-medium text-muted-foreground">목차</p>
      <ol className="flex flex-col gap-1">
        {sections.map((section, index) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              className="flex gap-2 rounded-md px-2 py-1.5 text-[13px] leading-[1.6] text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <span data-numeric="" className="w-3 shrink-0 font-mono text-[11.5px]">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">{section.heading}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * 절 안에 넣는 영상.
 *
 * 자동재생하되 **소리는 끈다.** 소리가 나면 읽던 사람이 놀라 탭을 닫는다.
 * 컨트롤은 남겨 둔다 — 자동으로 도는 화면을 멈추거나 되감을 수 없으면 그건 장식이지 증거가 아니다.
 *
 * 동작 최소화를 켠 사람에게는 자동재생하지 않는다. 첫 프레임만 두고 재생은 본인이 고르게 한다.
 *
 * 자막 트랙이 없다 — 대사 없는 화면 녹화라 옮길 말이 없다. 무엇이 찍혀 있는지는 figcaption 이 말한다.
 */
type MediaItem = NonNullable<PortfolioSection['media']>[number];

function SectionMedia({ item }: { item: MediaItem }): ReactElement {
  if (item.kind === 'image') {
    // 자를 비율은 사진마다 다르다 — 클래스로 만들면 Tailwind 가 못 훑어 규칙이 안 생긴다
    const cropStyle = item.crop
      ? {
          aspectRatio: item.crop.aspect,
          objectFit: 'cover' as const,
          objectPosition: item.crop.focus,
        }
      : undefined;

    return (
      <figure className="flex flex-col gap-2.5">
        <div className="overflow-hidden rounded-lg border border-border">
          <img
            src={item.src}
            alt={item.alt ?? item.caption}
            loading="lazy"
            style={cropStyle}
            className="block w-full"
          />
        </div>
        <figcaption className="text-[12.5px] leading-[1.7] text-muted-foreground">
          {item.caption}
        </figcaption>
      </figure>
    );
  }

  return <SectionVideo src={item.src} caption={item.caption} />;
}

function SectionVideo({ src, caption }: { src: string; caption: string }): ReactElement {
  // matchMedia 가 없는 환경(jsdom 등)에서는 조용히 자동재생 쪽으로 둔다 —
  // 여기서 던지면 문서 전체가 안 그려진다
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <figure className="flex flex-col gap-2.5">
      <div className="overflow-hidden rounded-lg border border-border bg-secondary">
        <video
          src={src}
          controls
          muted
          loop
          playsInline
          preload="metadata"
          autoPlay={!prefersReducedMotion}
          className="block w-full"
        />
      </div>
      <figcaption className="text-[12.5px] leading-[1.7] text-muted-foreground">
        {caption}
      </figcaption>
    </figure>
  );
}

/**
 * 기능 목록.
 *
 * 꾸미지 않는다. 이름과 한 줄 설명이면 무엇이 있는지는 다 전달된다 — 색 막대나 밑줄을 더하면
 * 목록이 아니라 배너처럼 읽힌다.
 *
 * 이름은 그 기능이 실제로 있는 화면으로 건다. 확인하러 갈 수 있어야 「있습니다」가
 * 주장이 아니라 사실이 된다. 밑줄은 가리키거나 탭으로 옮겨 왔을 때만 보인다.
 */
function FeatureList({
  items,
}: {
  items: NonNullable<PortfolioSection['features']>;
}): ReactElement {
  return (
    <dl className="flex flex-col gap-3">
      {items.map((item) => (
        <div
          key={item.name}
          className="flex flex-col gap-0.5 sm:grid sm:grid-cols-[4.5rem_minmax(0,1fr)] sm:gap-x-5"
        >
          <dt className="text-[14.5px] font-semibold tracking-[-0.02em]">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline-offset-4 hover:underline focus-visible:underline"
            >
              {item.name}
            </a>
          </dt>
          <dd className="text-[14.5px] leading-[1.85] text-foreground/75">{item.description}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * 본문 한 절.
 *
 * 절 머리에 가는 선을 긋고 번호를 제목 **위**에 올린다. 번호를 제목 옆에 붙이면 한 덩어리로
 * 읽혀서, 스크롤할 때 절이 바뀌는 지점이 안 보인다. 선 하나가 그 경계를 만든다.
 *
 * 본문은 흐린 회색으로 두지 않는다. 짧은 설명문이면 몰라도 이만한 길이를 흐리게 깔면
 * 처음부터 안 읽히는 글이 된다.
 */
export function DocSection({
  index,
  section,
}: {
  index: number;
  section: PortfolioSection;
}): ReactElement {
  const { media, features } = section;

  return (
    <section
      id={section.id}
      aria-labelledby={`${section.id}-heading`}
      className="flex scroll-mt-24 flex-col gap-4 border-border border-t pt-8"
    >
      <div className="flex flex-col gap-1.5">
        <span data-numeric="" className="font-mono text-[11.5px] font-medium text-primary">
          {String(index).padStart(2, '0')}
        </span>
        <h2
          id={`${section.id}-heading`}
          className="text-[clamp(1.25rem,1.1rem+0.6vw,1.5rem)] font-bold leading-[1.4] tracking-[-0.038em]"
        >
          {section.heading}
        </h2>
      </div>

      {section.paragraphs.map((paragraph, order) => (
        <Fragment key={paragraph.slice(0, 24)}>
          {media
            ?.filter((item) => item.before === order)
            .map((item) => (
              <SectionMedia key={item.src} item={item} />
            ))}
          <p className="text-[15.5px] leading-[2] text-foreground/80">
            <RichText text={paragraph} />
          </p>
        </Fragment>
      ))}

      {features ? <FeatureList items={features} /> : null}
    </section>
  );
}
